import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ArrowLeft, ShieldCheck, Mail, Phone, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { getDataRequests, updateDataRequest, deleteDataRequest } from '../utils/api';
import { getCurrentUser } from '../utils/api';

const TYPE_LABEL = {
  access: 'Access', correction: 'Correction', erasure: 'Erasure',
  withdraw_consent: 'Withdraw consent', nomination: 'Nomination', grievance: 'Grievance',
};
const STATUS = ['new', 'in_progress', 'resolved'];
const STATUS_STYLE = {
  new: 'bg-amber-100 text-amber-700',
  in_progress: 'bg-blue-100 text-blue-700',
  resolved: 'bg-green-100 text-green-700',
};

const AdminDataRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const isAdmin = getCurrentUser()?.role === 'admin';

  const load = async () => {
    try { setRequests(await getDataRequests()); }
    catch (err) { toast.error('Failed to load: ' + err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { document.title = 'Data Requests - Rubicon Finservices'; load(); }, []);

  const changeStatus = async (id, status) => {
    try { await updateDataRequest(id, { status }); setRequests((r) => r.map((x) => x.id === id ? { ...x, status } : x)); toast.success('Status updated'); }
    catch (err) { toast.error('Update failed: ' + err.message); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this data request permanently?')) return;
    try { await deleteDataRequest(id); setRequests((r) => r.filter((x) => x.id !== id)); toast.success('Request deleted'); }
    catch (err) { toast.error('Delete failed: ' + err.message); }
  };

  return (
    <div className="min-h-screen bg-slate-50 p-8" data-testid="admin-data-requests">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <ShieldCheck className="text-blue-600" size={28} />
            <h1 className="text-3xl font-bold text-slate-900">Data Rights Requests</h1>
          </div>
          <Button variant="outline" onClick={() => navigate('/admin/dashboard')}><ArrowLeft size={18} className="mr-2" /> Dashboard</Button>
        </div>
        <p className="text-slate-600 mb-6 text-sm">DPDP Act, 2023 — respond within 90 days. Requests come from the public Data Rights form.</p>

        {loading ? (
          <div className="flex justify-center py-16"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>
        ) : requests.length === 0 ? (
          <Card><CardContent className="p-12 text-center text-slate-500">No data requests yet.</CardContent></Card>
        ) : (
          <div className="space-y-4">
            {requests.map((r) => (
              <Card key={r.id} data-testid={`data-request-${r.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-bold text-slate-900">{r.name}</span>
                        <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-700 font-semibold">{TYPE_LABEL[r.request_type] || r.request_type}</span>
                        <span className={`text-xs px-2 py-0.5 rounded font-semibold ${STATUS_STYLE[r.status] || 'bg-slate-100'}`}>{(r.status || 'new').replace('_', ' ')}</span>
                      </div>
                      <div className="flex flex-wrap gap-4 text-sm text-slate-600">
                        <a href={`mailto:${r.email}`} className="flex items-center gap-1 hover:text-blue-600"><Mail size={14} /> {r.email}</a>
                        {r.phone && <span className="flex items-center gap-1"><Phone size={14} /> {r.phone}</span>}
                        <span className="text-slate-400">{new Date(r.created_at).toLocaleString('en-IN')}</span>
                      </div>
                      {r.details && <p className="text-sm text-slate-700 mt-2 bg-slate-50 rounded-lg p-3">{r.details}</p>}
                    </div>
                    <div className="flex gap-2">
                      {STATUS.map((s) => (
                        <button
                          key={s}
                          onClick={() => changeStatus(r.id, s)}
                          disabled={r.status === s}
                          className={`text-xs px-3 py-1.5 rounded-lg border font-semibold transition-colors ${r.status === s ? 'bg-slate-900 text-white border-slate-900' : 'border-slate-300 text-slate-600 hover:bg-slate-100'}`}
                          data-testid={`data-request-${r.id}-status-${s}`}
                        >
                          {s.replace('_', ' ')}
                        </button>
                      ))}
                      {isAdmin && (
                        <button
                          onClick={() => remove(r.id)}
                          className="text-xs px-2 py-1.5 rounded-lg border border-red-200 text-red-600 hover:bg-red-50 transition-colors"
                          data-testid={`data-request-${r.id}-delete`}
                          title="Delete"
                        >
                          <Trash2 size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminDataRequests;
